import clone from 'clone'
import { convertCase } from '../../_utils/utils'
import { stripUndefined } from '../../_utils/strip-undefined'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'

interface IGroupSegment {
    id: string
    account_id: number
    domain_id: number
    domain_name: string
    domain_display_name: string
    name: string
    estimated_reach: number | null
    computed_status: string
}

export class OrgSegmentModel {
    public static build(props: OrgSegmentModel | any): OrgSegmentModel {
        const data = props instanceof OrgSegmentModel ? props.serialize() : clone(props)
        const scData = convertCase(data, 'snake')

        const model = new OrgSegmentModel()
        model.id = scData.id
        model.accountId = scData.account_id
        model.setName(scData.name)
        model.setFiltersJson(data.filters_json ?? data.filtersJson)
        model.setFiltersHash(scData.filters_hash)
        model.setIconUrl(scData.icon_url)
        model.setMeta(data.meta)
        model.lastTargetedDateUtc = scData.last_targeted_date_utc
        model.isMultiDomain = scData.is_multi_domain
        model.setSource(scData.source)
        model.setChannels(data?.channels ?? [])
        model.setSegments(scData.segments ?? [])
        model.createdAt = scData.created_at
        model.createdBy = scData.created_by
        model.createdByUserName = scData.created_by_user_name
        model.updatedAt = scData.updated_at
        model.updatedBy = scData.updated_by
        model.updatedByUserName = scData.updated_by_user_name

        return model
    }

    private id: number
    private accountId: number
    private name: string
    private filtersJson: any
    private filtersHash: string
    private iconUrl: string | null
    private meta: any
    private lastTargetedDateUtc: string | null
    private isMultiDomain: boolean
    private source: string
    private channels: DeliveryChannel[]
    private segments: IGroupSegment[]
    private createdAt: string
    private createdBy: number
    private createdByUserName: string
    private updatedAt: string | null
    private updatedBy: number | null
    private updatedByUserName: string | null

    public clone(): OrgSegmentModel {
        return OrgSegmentModel.build(this)
    }

    public serialize(_: boolean = true): any {
        return stripUndefined({
            id: this.getId(),
            account_id: this.getAccountId(),
            name: this.getName(),
            filters_json: this.getFiltersJson(),
            filters_hash: this.getFiltersHash(),
            icon_url: this.getIconUrl(),
            meta: this.getMeta(),
            last_targeted_date_utc: this.getLastTargetedDateUtc(),
            is_multi_domain: this.getIsMultiDomain(),
            source: this.getSource(),
            channels: this.getChannels(),
            segments: this.getSegments(),
            created_at: this.getCreatedAt(),
            created_by: this.getCreatedBy(),
            created_by_user_name: this.getCreatedByUserName(),
            updated_at: this.getUpdatedAt(),
            updated_by: this.getUpdatedBy(),
            updated_by_user_name: this.getUpdatedByUserName(),
        })
    }

    public getId() {
        return this.id
    }
    public getAccountId() {
        return this.accountId
    }

    public getName() {
        return this.name
    }
    public setName(name: string) {
        this.name = name
    }

    public getFiltersJson() {
        return this.filtersJson ?? {}
    }
    public setFiltersJson(filters: any) {
        this.filtersJson = filters
    }

    public getFiltersHash() {
        return this.filtersHash
    }
    public setFiltersHash(hash: string) {
        this.filtersHash = hash
    }

    public getIconUrl() {
        return this.iconUrl
    }
    public setIconUrl(url: string | null) {
        this.iconUrl = url
    }

    public getMeta() {
        return this.meta
    }
    public setMeta(meta: any) {
        this.meta = meta
    }

    public getLastTargetedDateUtc() {
        return this.lastTargetedDateUtc
    }

    public getIsMultiDomain() {
        return this.isMultiDomain ?? false
    }

    public getSource() {
        return this.source
    }
    public setSource(source: string) {
        this.source = source
    }

    public getChannels() {
        return this.channels
    }
    public setChannels(deliveryChannels: DeliveryChannel[]): void {
        this.channels = deliveryChannels
    }

    public getSegments() {
        return this.segments ?? []
    }

    public setSegments(segments: IGroupSegment[]) {
        this.segments = segments
    }

    public getCreatedAt() {
        return this.createdAt
    }
    public getCreatedBy() {
        return this.createdBy
    }
    public getCreatedByUserName() {
        return this.createdByUserName
    }

    public getUpdatedAt() {
        return this.updatedAt
    }
    public getUpdatedBy() {
        return this.updatedBy
    }
    public getUpdatedByUserName() {
        return this.updatedByUserName
    }

    public getAggregateReach(): number {
        const children = this.getSegments() ?? []
        return children.reduce((reachAgg, { estimated_reach }) => (reachAgg += estimated_reach ?? 0), 0)
    }

    public getComputedStatus(): 'READY' | 'PROCESSING' {
        const children = this.getSegments() ?? []
        const uqStatuses = Array.from(new Set(children.map((segment) => segment.computed_status))).filter((s) => !!s) // remove any possible non-status values

        return uqStatuses.length === 0 || uqStatuses.includes('PROCESSING') ? 'PROCESSING' : 'READY'
    }

    public getDomainIds(): number[] {
        const children = this.getSegments() ?? []
        return children.map((segment) => segment.domain_id)
    }
}
